class ReviewBoxes {
  constructor(containerId = "reviewBoxesContainer") {
    this.containerId = containerId;
    this.reviews = [];
    this.currentPage = 1;
    this.currentFilter = "recent";
    this.currentLimit = 10;
    this.totalReviews = 0;
    this.isLoading = false;
    this.hasNext = false;
    this.hasPrev = false;
    this.handle = null;
    this.apiUrl = null;
    this.init();
  }

  init() {
    const container = document.getElementById(this.containerId);
    if (container) {
      this.handle = document.location.pathname.split('/').filter(part => part !== '').pop();
      this.apiUrl = "https://api.ultimate.thetabrands.com/reviews";
    }
    this.fetchReviews();
  }

  async fetchReviews() {
    this.isLoading = true;
    this.updateLoadingState();

    try {
      const separator = this.apiUrl.includes("?") ? "&" : "?";
      let url = `${this.apiUrl}${separator}filter=${this.currentFilter}&limit=${this.currentLimit}&page=${this.currentPage}`;
      if (this.handle) {
        url += `&handle=${encodeURIComponent(this.handle)}`;
      }
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.reviews = data.reviews || [];
      this.hasNext = data.hasNext || false;
      this.hasPrev = data.hasPrev || false;
      // Try different possible field names for total count
      this.totalReviews =
        data.totalCount || data.total || data.count || this.reviews.length;

      this.render();
    } catch (error) {
      console.error("Error fetching reviews:", error);
      this.showError("Failed to load reviews. Please try again later.");
    } finally {
      this.isLoading = false;
      this.updateLoadingState();
    }
  }

  updateLoadingState() {
    const container = document.getElementById(this.containerId);
    if (!container) return;

    const loadingEl = container.querySelector(".loadingIndicator");

    if (this.isLoading) {
      if (!loadingEl) {
        const loading = document.createElement("div");
        loading.className = "loadingIndicator";
        loading.textContent = "Loading reviews...";
        container.appendChild(loading);
      }
    } else {
      if (loadingEl) {
        loadingEl.remove();
      }
    }
  }

  showError(message) {
    const container = document.getElementById(this.containerId);
    if (!container) return;

    const errorEl = container.querySelector(".errorMessage");
    if (errorEl) {
      errorEl.remove();
    }

    const error = document.createElement("div");
    error.className = "errorMessage";
    error.textContent = message;
    container.appendChild(error);

    setTimeout(() => {
      if (error.parentNode) {
        error.remove();
      }
    }, 5000);
  }

  createFilterControls() {
    const container = document.createElement("div");
    container.className = "filterControls";

    // Filter dropdown
    const filterLabel = document.createElement("label");
    filterLabel.htmlFor = "filterSelect";
    filterLabel.textContent = "Sort by:";

    const filterSelect = document.createElement("select");
    filterSelect.title = "Sort By";
    filterSelect.id = "filterSelect";

    const filterOptions = [
      { value: "recent", label: "Most Recent" },
      { value: "highest", label: "Highest Rated" },
      { value: "lowest", label: "Lowest Rated" },
      { value: "media", label: "With Media" },
    ];

    filterOptions.forEach((option) => {
      const opt = document.createElement("option");
      opt.value = option.value;
      opt.textContent = option.label;
      if (option.value === this.currentFilter) opt.selected = true;
      filterSelect.appendChild(opt);
    });

    // Limit dropdown
    const limitLabel = document.createElement("label");
    limitLabel.textContent = "Review count:";
    limitLabel.htmlFor = "limitSelect";

    const limitSelect = document.createElement("select");
    limitSelect.title = "Number of Reviews";
    limitSelect.id = "limitSelect";

    [10, 20, 50, 100].forEach((limit) => {
      const opt = document.createElement("option");
      opt.value = limit;
      opt.textContent = `${limit} reviews`;
      if (limit === this.currentLimit) opt.selected = true;
      limitSelect.appendChild(opt);
    });

    // Event listeners
    filterSelect.addEventListener("change", (e) => {
      this.currentFilter = e.target.value;
      this.currentPage = 1;
      this.fetchReviews();
    });

    limitSelect.addEventListener("change", (e) => {
      this.currentLimit = parseInt(e.target.value);
      this.currentPage = 1;
      this.fetchReviews();
    });

    container.appendChild(filterLabel);
    container.appendChild(filterSelect);
    container.appendChild(limitLabel);
    container.appendChild(limitSelect);

    return container;
  }

  renderStars(rating, size = "medium") {
    const container = document.createElement("div");
    container.className = "starContainer";
    container.style.display = "inline-flex";
    container.style.alignItems = "center";

    const clampedRating = Math.max(0, Math.min(5, rating));
    const starSize = size === "small" ? 16 : size === "large" ? 24 : 20;

    const starPath =
      "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z";

    for (let i = 1; i <= 5; i++) {
      let fillPercentage = 0;

      if (i <= clampedRating) {
        fillPercentage = 100;
      } else if (i - 1 < clampedRating) {
        fillPercentage = (clampedRating - (i - 1)) * 100;
      }

      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("width", starSize);
      svg.setAttribute("height", starSize);
      svg.setAttribute("viewBox", "0 0 24 24");
      svg.classList.add("star-svg");
      svg.setAttribute("title", `${clampedRating} stars`);
      svg.style.marginRight = "2px";

      // Background star (unfilled)
      const bgPath = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path"
      );
      bgPath.setAttribute("d", starPath);
      bgPath.setAttribute("fill", "#e1e1e1");
      bgPath.setAttribute("stroke", "#e1e1e1");
      bgPath.setAttribute("stroke-width", "0.5");
      svg.appendChild(bgPath);

      // Filled portion
      if (fillPercentage > 0) {
        const fgPath = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "path"
        );
        fgPath.setAttribute("d", starPath);
        fgPath.setAttribute("fill", "#fabf46");
        fgPath.setAttribute("stroke", "#fabf46");
        fgPath.setAttribute("stroke-width", "0.5");

        if (fillPercentage < 100) {
          fgPath.style.clipPath = `inset(0 ${100 - fillPercentage}% 0 0)`;
        }

        svg.appendChild(fgPath);
      }

      container.appendChild(svg);
    }

    return container;
  }

  renderPagination() {
    if (!this.hasNext && !this.hasPrev) return null;

    const container = document.createElement("div");
    container.className = "pagination";

    const prevBtn = document.createElement("button");
    prevBtn.textContent = "← Previous";
    prevBtn.disabled = !this.hasPrev;

    const pageInfo = document.createElement("span");
    pageInfo.textContent = `Page ${this.currentPage}`;

    const nextBtn = document.createElement("button");
    nextBtn.textContent = "Next →";
    nextBtn.disabled = !this.hasNext;

    prevBtn.addEventListener("click", () => {
      if (this.hasPrev) {
        this.currentPage--;
        this.fetchReviews();
      }
    });

    nextBtn.addEventListener("click", () => {
      if (this.hasNext) {
        this.currentPage++;
        this.fetchReviews();
      }
    });

    container.appendChild(prevBtn);
    container.appendChild(pageInfo);
    container.appendChild(nextBtn);

    return container;
  }

  renderReview(review) {
    const reviewBox = document.createElement("div");
    reviewBox.className = "reviewBox";

    // Review header
    const header = document.createElement("div");
    header.className = "reviewHeader";

    const avatar = document.createElement("div");
    avatar.className = "userAvatar";
    avatar.textContent = review.name
      .split(" ")
      .map((name) => name[0])
      .join("");

    const reviewerInfo = document.createElement("div");
    reviewerInfo.className = "reviewerInfo";

    const reviewerName = document.createElement("h3");
    reviewerName.className = "reviewerName";
    reviewerName.textContent = review.name;

    const reviewDate = document.createElement("div");
    reviewDate.className = "reviewDate";
    reviewDate.textContent = new Date(review.date).toLocaleDateString();

    reviewerInfo.appendChild(reviewerName);
    reviewerInfo.appendChild(reviewDate);

    header.appendChild(avatar);
    header.appendChild(reviewerInfo);

    // Review rating and title
    const ratingDiv = document.createElement("div");
    ratingDiv.className = "reviewRating";

    const starsContainer = this.renderStars(review.rating);
    ratingDiv.appendChild(starsContainer);

    if (review.title) {
      const reviewTitle = document.createElement("h4");
      reviewTitle.className = "reviewTitle";
      reviewTitle.textContent = review.title;
      ratingDiv.appendChild(reviewTitle);
    }

    // Review images (if any)
    let imagesDiv = null;
    if (review.imageUrl && review.imageUrl.trim() !== "") {
      const imageUrls = review.imageUrl
        .split(",")
        .map((url) => url.trim())
        .filter((url) => url !== "");

      if (imageUrls.length > 0) {
        imagesDiv = document.createElement("div");
        imagesDiv.className = "reviewImages";

        imageUrls.forEach((imageUrl, index) => {
          const img = document.createElement("img");
          img.src = imageUrl;
          img.alt = review.title;
          img.className = "reviewImage";
          img.addEventListener("click", () => {
            openSimpleLightBox(imageUrl, review.title);
          });
          imagesDiv.appendChild(img);
        });
      }
    }

    // Review text
    const reviewText = document.createElement("p");
    reviewText.className = "reviewText";
    reviewText.textContent = review.body;

    // Assemble the review box
    reviewBox.appendChild(header);
    reviewBox.appendChild(ratingDiv);
    if (imagesDiv) reviewBox.appendChild(imagesDiv);
    reviewBox.appendChild(reviewText);

    return reviewBox;
  }

  render() {
    const container = document.getElementById(this.containerId);
    if (!container) return;

    // Clear existing content
    container.innerHTML = "";

    container.classList.remove("hidden");

    // Always add filter controls
    container.appendChild(this.createFilterControls());

    // Create review container
    const reviewContainer = document.createElement("div");
    reviewContainer.className = "reviewContainer";

    // Add reviews or no reviews message
    if (this.reviews.length === 0) {
      const noReviewsDiv = document.createElement("div");
      noReviewsDiv.className = "noReviews";
      noReviewsDiv.textContent = "No reviews found for the selected filter.";
      reviewContainer.appendChild(noReviewsDiv);
    } else {
      this.reviews.forEach((review) => {
        reviewContainer.appendChild(this.renderReview(review));
      });
    }

    container.appendChild(reviewContainer);

    // Add pagination only if there are reviews
    if (this.reviews.length > 0) {
      const pagination = this.renderPagination();
      if (pagination) {
        container.appendChild(pagination);
      }
    }
  }

  // Public method to refresh the component
  refresh() {
    this.fetchReviews();
  }
}

function openSimpleLightBox(src, alt) {
  let lightboxImg = document.createElement("img");
  lightboxImg.src = src;
  lightboxImg.alt = alt;
  document.getElementById("simpleLightBoxContainer").appendChild(lightboxImg);

  setTimeout(() => {
    document.getElementById("simpleLightBox").classList.add("show");
  }, 200);
}

function closeSimpleLightBox() {
  document.getElementById("simpleLightBox").classList.remove("show");

  setTimeout(() => {
    document.getElementById("simpleLightBox").getElementsByTagName("img")[0].remove()
  }, 200);
}

// Initialize when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  window.reviewBoxes = new ReviewBoxes();
});

// Export for use in other contexts
if (typeof module !== "undefined" && module.exports) {
  module.exports = ReviewBoxes;
}
